import { ApplicationContextListenerScope, ApplicationContext as CoreApplicationContext } from '@philips-internal/oswald-ui-core/lib/component/context/index.js';
import { AuthenticationService, HistoryService } from '@philips-internal/oswald-ui-core/lib/services/index.js';
import { isNumberValid } from '@philips-internal/oswald-ui-model/lib/generators/numeric/numeric-utils.js';
import { applicationModuleRegistry } from '../application-module-registry.js';
import { getAvailableModules, getMainRoute, mapRoutes } from '../util/index.js';
export class ApplicationContext extends CoreApplicationContext {
    constructor() {
        super(...arguments);
        this.hiddenModuleOptions = new Map();
        this.additionalModuleInformation = new Map();
        this.openModules = new Map();
    }
    /**
     * Close all currently open modules.
     */
    closeModules() {
        this.currentModule = undefined;
        this.openModules.clear();
        this.notify(ApplicationContextListenerScope.MODULES);
    }
    /**
     * Close a specific module, if open.
     *
     * @param id - the module id.
     */
    closeModule(id) {
        if (!this.openModules.has(id)) {
            return;
        }
        const availableModules = getAvailableModules();
        const module = applicationModuleRegistry.get(id);
        if (!availableModules.has(id)) {
            throw Error(`[Application Context]: Module ${id} is not available.`);
        }
        if (!module.closeable) {
            throw Error(`[Application Context]: Module ${id} can't be closed.`);
        }
        if (this.currentModule && this.currentModule.id !== id) {
            this.openModules.delete(id);
            return this.notify(ApplicationContextListenerScope.MODULES);
        }
        const modules = this.getOpenModules();
        const moduleIndex = modules.findIndex((module) => module.id === id);
        const previousModule = modules[moduleIndex - 1];
        const nextModule = modules[moduleIndex + 1];
        // Delete after `getOpenModules`, otherwise can't get the module being closed index;
        this.openModules.delete(id);
        if (!previousModule && !nextModule) {
            AuthenticationService.redirectToHome();
            return this.notify(ApplicationContextListenerScope.MODULES);
        }
        // There's no need to call `notify` for the two next statements, as the `openModule` function
        // already calls it.
        if (previousModule) {
            return this.openModule(previousModule.id);
        }
        if (nextModule) {
            return this.openModule(nextModule.id);
        }
    }
    /**
     * Obtain the current application module.
     */
    getCurrentModule() {
        return this.currentModule;
    }
    /**
     * Get the additional module information.
     *
     * @param moduleId - the module id
     */
    getAdditionalModuleInformation(moduleId) {
        return this.additionalModuleInformation.get(moduleId);
    }
    /**
     * Obtain all of the hidden options of a give module.
     *
     * @param module - the module id
     */
    getModuleHiddenOptions(module) {
        return this.hiddenModuleOptions.get(module);
    }
    /**
     * Get all of the application open modules.
     */
    getOpenModules() {
        return Array.from(this.openModules.values());
    }
    /**
     * Hide a option of a given module.
     *
     * @param moduleId - the module id.
     * @param optionName - the option name.
     */
    hideModuleOption(moduleId, optionName) {
        const hiddenOptions = this.hiddenModuleOptions.get(moduleId) ?? new Set();
        hiddenOptions.add(optionName);
        this.hiddenModuleOptions.set(moduleId, hiddenOptions);
        this.notify(ApplicationContextListenerScope.OPTIONS);
    }
    /**
     * Open a new module, respecting the modules available for the user.
     *
     * Also, when opening a module, this method will also trigger a request for a new authentication token, updating it
     * with the new module id.
     *
     * @param id - the module id
     */
    openModule(id, options) {
        const availableModules = getAvailableModules();
        const module = applicationModuleRegistry.get(id);
        if (!availableModules.has(module.id)) {
            throw Error(`[Application Context]: Module ${id} is not available.`);
        }
        const parameters = {
            'ext_module_id': module.id,
        };
        void AuthenticationService.requestRefreshToken(parameters).then(() => {
            this.openModules.set(id, module);
            if (options?.preventNavigation) {
                return this.notify(ApplicationContextListenerScope.MODULES);
            }
            const previousModule = this.currentModule;
            const { pathname: previousPath = '' } = HistoryService.getMatch();
            this.currentModule = module;
            const mappedRoutes = mapRoutes(module.id, module.route, module.routes);
            const mainRoute = getMainRoute(mappedRoutes);
            if (mainRoute) {
                HistoryService.push(mainRoute.completeRoute ?? mainRoute.route);
            }
            else {
                HistoryService.push(module.route);
            }
            this.notify(ApplicationContextListenerScope.MODULES);
            if (!options?.at || options.at.skip) {
                return;
            }
            const { options: atOptions, params, path } = options.at;
            HistoryService.push(path, params, atOptions);
            if (previousModule && this.currentModule.id !== previousModule.id) {
                this.pushNavigationItem(() => {
                    this.openModule(previousModule.id, {
                        at: { options: atOptions, path: previousPath, skip: true },
                    });
                    this.notifyRedirect({ path: previousPath });
                });
            }
            this.notifyRedirect({ path });
        });
    }
    /**
     * Set the badge of a given module.
     *
     * @param moduleId - the module id.
     */
    setBadge(moduleId, options) {
        const availableModules = getAvailableModules();
        if (!availableModules.has(moduleId)) {
            return;
        }
        const moduleInformation = this.getAdditionalModuleInformation(moduleId) ?? {};
        moduleInformation.badge = isNumberValid(options.count) ? options.count : undefined;
        this.additionalModuleInformation.set(moduleId, moduleInformation);
        this.notify(ApplicationContextListenerScope.OPTIONS);
    }
    /**
     * Show the option of a given module.
     *
     * @param moduleId - the module id.
     * @param optionName - the option name.
     */
    showModuleOption(moduleId, optionName) {
        const hiddenOptions = this.hiddenModuleOptions.get(moduleId) ?? new Set();
        hiddenOptions.delete(optionName);
        this.hiddenModuleOptions.set(moduleId, hiddenOptions);
        this.notify(ApplicationContextListenerScope.OPTIONS);
    }
    /**
     * Updates the disabled state of a module.
     *
     * @param moduleId - The module id
     * @param disabled - The updated disabled state.
     */
    setDisabledModule(moduleId, disabled) {
        applicationModuleRegistry.setDisabledModule(moduleId, disabled);
        this.notify(ApplicationContextListenerScope.MODULES);
    }
    /**
     * Returns the disabled state of a module.
     *
     * @param moduleId - The module id.
     */
    isDisabledModule(moduleId) {
        return applicationModuleRegistry.isDisabledModule(moduleId);
    }
    /**
     * Updates the hidden state of a module.
     *
     * @param moduleId - The module id
     * @param hidden - The updated hidden state.
     */
    setHiddenModule(moduleId, hidden) {
        applicationModuleRegistry.setHiddenModule(moduleId, hidden);
        this.notify(ApplicationContextListenerScope.MODULES);
    }
    /**
     * Returns the hidden state of a module.
     *
     * @param moduleId - The module id.
     */
    isHiddenModule(moduleId) {
        return applicationModuleRegistry.isHiddenModule(moduleId);
    }
}
const applicationContext = new ApplicationContext();
export { applicationContext, };
//# sourceMappingURL=application-context.js.map